'use client'

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Spinner from '../components/Spinner';
import Button from '../components/Button';
import api from '../api/axios';

// ── Constants ─────────────────────────────────────────────────────────────────

const RESOURCE_TYPE_OPTIONS = [
  { value: '', label: 'All resources' },
  { value: 'CONNECTOR',      label: 'Connector' },
  { value: 'OPT_OUT_POLICY', label: 'Opt-Out Policy' },
  { value: 'SENDER',         label: 'Sender' },
  { value: 'TEAM_MEMBER',    label: 'Team Member' },
  { value: 'TEMPLATE',       label: 'Template' },
];

const ACTION_OPTIONS = [
  { value: '',       label: 'All actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
];

const PAGE_SIZES = [10, 25, 50, 100];

const RESOURCE_LABELS = {
  TEMPLATE:       'Template',
  SENDER:         'Sender',
  CONNECTOR:      'Connector',
  OPT_OUT_POLICY: 'Opt-Out Policy',
  TEAM_MEMBER:    'Team Member',
};

const ACTION_STYLES = {
  CREATE: 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300',
  UPDATE: 'bg-amber-100  text-amber-800  dark:bg-amber-900/60  dark:text-amber-300',
  DELETE: 'bg-red-100   text-red-800   dark:bg-red-900/60   dark:text-red-300',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function ActionBadge({ action }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${ACTION_STYLES[action] ?? 'bg-gray-100 text-gray-600'}`}>
      {action}
    </span>
  );
}

function FilterInput({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

function Pagination({ page, totalPages, totalElements, pageSize, onPageChange, onPageSizeChange }) {
  const start = totalElements === 0 ? 0 : page * pageSize + 1;
  const end   = Math.min((page + 1) * pageSize, totalElements);
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-800 text-sm text-gray-600 dark:text-gray-400">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">Rows per page:</span>
        <select
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value))}
          className="border border-gray-200 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm"
        >
          {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <span className="text-xs">
        {totalElements === 0 ? 'No results' : `${start}–${end} of ${totalElements.toLocaleString()}`}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(0)}          disabled={page === 0}                 className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">«</button>
        <button onClick={() => onPageChange(page - 1)}   disabled={page === 0}                 className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">‹</button>
        <span className="px-3 text-xs">Page {page + 1} of {Math.max(totalPages, 1)}</span>
        <button onClick={() => onPageChange(page + 1)}   disabled={page >= totalPages - 1}     className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">›</button>
        <button onClick={() => onPageChange(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 rounded disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800">»</button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const EMPTY_FILTERS = { performedBy: '', resourceType: '', action: '', resourceName: '', from: '', to: '' };
const DEFAULT_SORT  = { field: 'timestamp', dir: 'desc' };

// ── Sortable column header ────────────────────────────────────────────────────

function SortIcon({ state }) {
  if (state === 'asc')  return <span className="ml-1 text-emerald-500">↑</span>;
  if (state === 'desc') return <span className="ml-1 text-emerald-500">↓</span>;
  return <span className="ml-1 text-gray-300 dark:text-gray-600">↕</span>;
}

function SortableHeader({ label, field, sort, onSort }) {
  const active = sort.field === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
    >
      {label}
      <SortIcon state={active ? sort.dir : null} />
    </th>
  );
}

export default function AuditLog() {
  const navigate         = useNavigate();
  const activeAccountId  = useSelector(s => s.globalData.activeAccountId);

  const [filters,     setFilters]     = useState(EMPTY_FILTERS);
  const [lastFilters, setLastFilters] = useState(EMPTY_FILTERS);
  const [results,     setResults]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [page,        setPage]        = useState(0);
  const [pageSize,    setPageSize]    = useState(25);
  const [sort,        setSort]        = useState(DEFAULT_SORT);

  // ── Fetch ──

  const fetchEvents = useCallback(async (f, pg, ps, srt) => {
    if (!activeAccountId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, size: ps, sort: `${srt.field},${srt.dir}` });
      if (f.performedBy)  params.set('performedBy',  f.performedBy.trim());
      if (f.resourceType) params.set('resourceType', f.resourceType);
      if (f.action)       params.set('action',       f.action);
      if (f.resourceName) params.set('resourceName', f.resourceName.trim());
      if (f.from) params.set('from', new Date(f.from).toISOString());
      if (f.to)   params.set('to',   new Date(f.to).toISOString());

      const res = await api.get(
        `${process.env.REACT_APP_BASE_API_URL}/mvapi/audit/events/${activeAccountId}?${params}`
      );
      setResults(res.data);
    } catch (err) {
      console.error('Audit fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeAccountId]);

  // Auto-load on account change
  useEffect(() => {
    if (activeAccountId) {
      fetchEvents(EMPTY_FILTERS, 0, 25, DEFAULT_SORT);
    }
  }, [activeAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    setLastFilters(filters);
    setPage(0);
    fetchEvents(filters, 0, pageSize, sort);
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setLastFilters(EMPTY_FILTERS);
    setPage(0);
    setSort(DEFAULT_SORT);
    fetchEvents(EMPTY_FILTERS, 0, pageSize, DEFAULT_SORT);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchEvents(lastFilters, newPage, pageSize, sort);
  };

  const handlePageSizeChange = newSize => {
    setPageSize(newSize);
    setPage(0);
    fetchEvents(lastFilters, 0, newSize, sort);
  };

  const handleSort = field => {
    const newSort = sort.field === field
      ? { field, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'desc' };
    setSort(newSort);
    setPage(0);
    fetchEvents(lastFilters, 0, pageSize, newSort);
  };

  // ── Render ──

  const rows = results?.content ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8">

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h2 className="text-2xl/7 font-bold text-gray-900 dark:text-gray-100 sm:truncate sm:text-3xl sm:tracking-tight">
            Audit Log
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            A full history of configuration changes made within this account.
          </p>
        </div>
      </div>

      {/* Filter panel */}
      <form
        onSubmit={handleSubmit}
        className="bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800 p-5 mb-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <FilterInput label="Performed by">
            <input
              type="text"
              name="performedBy"
              value={filters.performedBy}
              onChange={handleFilterChange}
              placeholder="Name or email…"
              className={inputCls}
            />
          </FilterInput>

          <FilterInput label="Resource">
            <select name="resourceType" value={filters.resourceType} onChange={handleFilterChange} className={inputCls}>
              {RESOURCE_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FilterInput>

          <FilterInput label="Action">
            <select name="action" value={filters.action} onChange={handleFilterChange} className={inputCls}>
              {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </FilterInput>

          <FilterInput label="Resource name">
            <input
              type="text"
              name="resourceName"
              value={filters.resourceName}
              onChange={handleFilterChange}
              placeholder="Search by name…"
              className={inputCls}
            />
          </FilterInput>

          <FilterInput label="From">
            <input
              type="datetime-local"
              name="from"
              value={filters.from}
              onChange={handleFilterChange}
              className={inputCls}
            />
          </FilterInput>

          <FilterInput label="To">
            <input
              type="datetime-local"
              name="to"
              value={filters.to}
              onChange={handleFilterChange}
              className={inputCls}
            />
          </FilterInput>
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={handleClear}>
            Clear
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </Button>
        </div>
      </form>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {/* Results */}
      {!loading && results && (
        <div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
            {results.totalElements.toLocaleString()} event{results.totalElements !== 1 ? 's' : ''} found
          </p>

          <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-sm">
                <thead className="bg-gray-50 dark:bg-gray-950">
                  <tr>
                    <SortableHeader label="Timestamp"     field="timestamp"    sort={sort} onSort={handleSort} />
                    <SortableHeader label="Action"        field="action"       sort={sort} onSort={handleSort} />
                    <SortableHeader label="Resource"      field="resourceType" sort={sort} onSort={handleSort} />
                    <SortableHeader label="Resource name" field="resourceName" sort={sort} onSort={handleSort} />
                    <SortableHeader label="Performed by"  field="performedBy"  sort={sort} onSort={handleSort} />
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-black">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center text-gray-400 dark:text-gray-500">
                        No audit events found.
                      </td>
                    </tr>
                  ) : rows.map(event => (
                    <tr
                      key={event.id}
                      onClick={() => navigate(`/audit-log/${event.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-900 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400 text-xs">
                        {event.timestamp ? new Date(event.timestamp).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ActionBadge action={event.action} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {RESOURCE_LABELS[event.resourceType] ?? event.resourceType ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate" title={event.resourceName}>
                        {event.resourceName ?? '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">
                        {event.performedBy ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:underline">
                          View →
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={results.totalPages}
              totalElements={results.totalElements}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
